import { useCallback, useState } from "react";
import { FetchPageParams } from "@platform/design-system";
import { useApi } from "@platform/api";

import { Client, formatResponse } from "../pages/Clients/utils";

import type { SortModelItem } from "ag-grid-community";

type UseFetchClientsProps = {
  search?: string;
  brokerId?: number | string;
  callbackFn?: (params: {
    search?: string;
    sortModel?: SortModelItem[];
  }) => void;
};

type UseFetchClientsResponse = {
  fetchClientsPage: (params: FetchPageParams) => Promise<{
    rows: Client[];
    total: number;
  }>;
  total: number;
  isError: boolean;
};

export const useFetchClients = ({
  search,
  brokerId,
  callbackFn,
}: UseFetchClientsProps): UseFetchClientsResponse => {
  const axios = useApi();
  const [total, setTotal] = useState<number>(0);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchClientsPage = useCallback<
    (params: FetchPageParams) => Promise<{ rows: Client[]; total: number }>
  >(
    async ({ page, pageSize, sortModel }) => {
      try {
        const params = new URLSearchParams();
        params.append("pageNumber", page.toString());
        params.append("pageSize", pageSize.toString());

        if (search?.trim()) params.append("q", search.trim());

        if (brokerId) params.append("brokerId", brokerId.toString());
        if (sortModel?.length) {
          params.append("sortBy", sortModel[0].colId);
          params.append(
            "isDescending",
            (sortModel[0].sort === "desc").toString(),
          );
        }

        callbackFn?.({ sortModel, search });

        const { data } = await axios.get<{ total: number; clients: Client[] }>(
          `/brokerportal/v1/clients?${params.toString()}`,
        );

        setTotal(data.total);
        return { rows: formatResponse(data.clients), total: data.total };
      } catch {
        setIsError(true);
        return { rows: [], total: 0 };
      }
    },
    [brokerId, axios, callbackFn, search],
  );

  return {
    fetchClientsPage,
    total,
    isError,
  };
};
